import { t } from "translations"

import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Select from "@mui/material/Select"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import Icon from "Components/Icon/Icon"

export default function TopFilterSelect({
    templates,
    filter,
    selectFilter,
    resetFilter
}: {
    templates: string[]
    filter: string
    selectFilter: (templateName: string) => void
    resetFilter: () => void
}) {
    return (
        <StyledFormControl color="secondary">
            <InputLabel>
                <Typography
                    variant="subtitle2"
                    color={"black"}>
                    {t.archive.filterOnContentType}
                </Typography>
            </InputLabel>

            <StyledSelect
                IconComponent={props => (
                    <Icon
                        name="expandMore"
                        color="secondary"
                        {...props}
                    />
                )}
                color="secondary"
                value={filter}
                label={
                    <Typography variant="subtitle2">
                        {t.archive.filterOnContentType}
                    </Typography>
                }>
                <StyledMenuItem
                    onClick={resetFilter}
                    value={""}>
                    <Typography variant="subtitle2">
                        {t.archive.resetFilter}
                    </Typography>
                </StyledMenuItem>
                {templates.map(templateName => (
                    <StyledMenuItem
                        onClick={() => selectFilter(templateName)}
                        value={templateName}
                        key={templateName}>
                        <Typography variant="subtitle2">
                            {templateName}
                        </Typography>
                    </StyledMenuItem>
                ))}
            </StyledSelect>
        </StyledFormControl>
    )
}

const StyledFormControl = styled(FormControl)(() => ({
    width: 190,
    marginLeft: "auto",
    marginRight: 0
}))

const StyledSelect = styled(Select)(({ theme }) => ({
    "& .MuiOutlinedInput-notchedOutline": {
        border: `${theme.spacing(0.25)} solid `,
        borderRadius: theme.shape.borderRadius,
        borderColor: theme.palette.secondary.main,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    minHeight: theme.spacing(0),
    ":first-of-type": {
        color: theme.palette.secondary.main
    }
}))

export function LeftFilterSelect({
    templates,
    filter,
    selectFilter,
    resetFilter,
    isLoading
}: {
    templates: string[]
    filter: string
    selectFilter: (templateName: string) => void
    resetFilter: () => void
    isLoading: boolean
}) {
    return (
        <StyledLeftDiv>
            <StyledLeftAside>
                {isLoading ? (
                    <Skeleton
                        variant="rectangular"
                        height="100vh"
                    />
                ) : (
                    <>
                        <Typography variant="subtitle2">
                            {t.archive.filterOnContentType}
                        </Typography>
                        <StyledFacetsList>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label={t.archive.filterOnContentType}
                                    name="filter"
                                    value={filter}>
                                    <StyledFormControlLabel
                                        value={""}
                                        control={
                                            <StyledRadio color="secondary" />
                                        }
                                        label={
                                            <Typography variant="subtitle2">
                                                {t.archive.resetFilter}
                                            </Typography>
                                        }
                                        onClick={resetFilter}
                                    />
                                    {templates.map(templateName => (
                                        <StyledFormControlLabel
                                            key={templateName}
                                            value={templateName}
                                            control={
                                                <StyledRadio color="secondary" />
                                            }
                                            label={
                                                <Typography variant="subtitle2">
                                                    {templateName}
                                                </Typography>
                                            }
                                            onClick={() =>
                                                selectFilter(templateName)
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </StyledFacetsList>
                    </>
                )}
            </StyledLeftAside>
        </StyledLeftDiv>
    )
}

const StyledLeftDiv = styled("div")(({ theme }) => ({
    position: "relative",
    padding: theme.spacing(2)
}))

const StyledLeftAside = styled("div")(({ theme }) => ({
    position: "sticky",
    top: theme.constants.headerHeight,
    display: "none",
    [theme.breakpoints.up("md")]: {
        display: "block"
    }
}))

const StyledFacetsList = styled("div")(({ theme }) => ({
    padding: theme.spacing(2),
    border: `${theme.spacing(0.25)} solid ${theme.palette.secondary.main} `,
    borderRadius: theme.shape.borderRadius
}))

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    ":first-of-type": {
        color: theme.palette.secondary.dark,
        textDecoration: "underline",
        "&:hover": {
            textDecoration: "none"
        }
    }
}))

const StyledRadio = styled(Radio)(({ theme }) => ({
    padding: theme.spacing(0.5, 0.5, 0.5, 1)
}))
